import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { usePackageFinancialMetrics } from "@/hooks/usePackageFinancialMetrics";
import { DollarSign, TrendingUp, PiggyBank } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export const PackageFinancialAnalyticsWidget = () => {
  const { data: metrics, isLoading } = usePackageFinancialMetrics();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  const totalRevenue = metrics?.reduce((sum, m) => sum + m.sellingPrice, 0) || 0;
  const totalCost = metrics?.reduce((sum, m) => sum + m.baseCost, 0) || 0;
  const totalProfit = metrics?.reduce((sum, m) => sum + m.netProfit, 0) || 0;
  const averageROI = metrics?.reduce((sum, m) => sum + m.roi, 0) / (metrics?.length || 1) || 0;
  const profitMargin = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100) : 0;

  return (
    <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-600" />
          التحليلات المالية للباقات
        </CardTitle>
        <CardDescription>ملخص الإيرادات والتكاليف والأرباح</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <TrendingUp className="w-4 h-4" />
              إجمالي الإيرادات
            </div>
            <div className="text-2xl font-bold text-green-600 mt-2">
              {totalRevenue.toLocaleString()} ريال
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <DollarSign className="w-4 h-4" />
              إجمالي التكاليف
            </div>
            <div className="text-2xl font-bold text-red-600 mt-2">
              {totalCost.toLocaleString()} ريال
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <PiggyBank className="w-4 h-4" />
              صافي الربح
            </div>
            <div className="text-2xl font-bold text-blue-600 mt-2">
              {totalProfit.toLocaleString()} ريال
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-sm text-muted-foreground">هامش الربح</div>
            <div className="text-2xl font-bold text-purple-600 mt-2">
              {profitMargin.toFixed(1)}%
            </div>
          </div>
        </div>

        <div className="mt-6 bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold">متوسط العائد على الاستثمار (ROI)</h4>
            <span className="text-2xl font-bold text-green-600">{averageROI.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(averageROI, 100)}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
